import { STANDALONE_LINK_CLASS } from "@/components/layout/standalone-link";
import { AvatarBox } from "@/components/logo";
import { SupportWhatsAppLink } from "@/components/support/SupportWhatsAppLink";
import { beginConciergeSession, failConciergeSession } from "@/lib/analytics";
import { handleActionError } from "@/lib/errors";
import { setCapabilityToken } from "@/lib/concierge/capability-token";
import {
  clearLinkCredentials,
  readLinkCredentials
} from "@/lib/concierge/link-credentials";
import { ACTIVATE_NEXT, ACTIVATE_PATH } from "@/lib/navigation/routes";
import { profileQuery } from "@/queries/customer/customer";
import { queryClient } from "@/queries/queryClient";
import { Button, Heading, Text } from "@medusajs/ui";
import sdk, { isFetchError } from "@repo/api-sdk";
import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { rootRoute } from "../$rootRoute";

/**
 * The concierge landing route.
 *
 * A **public** sibling of `login` / `logout` / `reset-password`, deliberately
 * outside `authRoute`: its entire job is to create the session that gate
 * requires, so sitting behind it would bounce every salon straight to a login
 * form they have no password for.
 */
export const enterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "enter",
  // No `validateSearch`: the link's `token` and `cap` are not search params by
  // the time the router runs. They are lifted out of the URL before the app
  // starts so analytics can never read them (KAN-262) - see
  // `lib/concierge/link-credentials.ts`.
  component: EnterPage
});

type RedeemState = "redeeming" | "failed" | "missing-token";

/**
 * Copy for a link that cannot be redeemed.
 *
 * Says "ask for a fresh link", never "unauthorized": the salon has done nothing
 * wrong, and the only action that helps them is asking for a new one. The
 * server's message already says which of expired / replaced / unknown it was, so
 * it is shown verbatim when present.
 */
const FALLBACK_FAILURE_MESSAGE =
  "This link has expired. Ask Caroline for a fresh link.";

function EnterPage() {
  // `cap` is the concierge operator's ad hoc-add capability token (KAN-193),
  // present only on links minted for an operator - a salon-facing link never
  // carries it. See `lib/concierge/capability-token.ts`.
  const [{ token, cap, next }] = useState(readLinkCredentials);
  const isActivation = next === ACTIVATE_NEXT;
  const navigate = useNavigate();

  const [state, setState] = useState<RedeemState>(
    token ? "redeeming" : "missing-token"
  );
  const [message, setMessage] = useState(FALLBACK_FAILURE_MESSAGE);
  // Set when the server read the link and refused it (a 4xx). An activation link
  // that failed because we could not reach the server is not "no longer valid",
  // and saying so would send the salon off to contact us over a blip.
  const [serverRejectedLink, setServerRejectedLink] = useState(false);
  // A link with no token never reached the server but is just as dead.
  const linkIsDead = state === "missing-token" || serverRejectedLink;
  // What the support message names. A salon with a dead activation link is not
  // "on the Your Cart Link page": naming the activation step tells support it is
  // an activation link that needs replacing.
  const supportPath = isActivation ? ACTIVATE_PATH : "/enter";

  // One redemption per mounted page, and therefore one navigate.
  //
  // Deliberately NOT paired with a `cancelled` flag in cleanup. StrictMode runs
  // effects setup -> cleanup -> setup while preserving refs, so a cleanup that
  // cancelled the in-flight redemption would make the resolved promise bail
  // before navigating, while the second setup early-returned on this ref -
  // leaving the salon on "Opening your cart..." forever in development. This
  // ref is the only guard needed: it already makes the double navigate the
  // cancellation was there to prevent impossible.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    // No link to redeem: the actor is not going to be learned, so start
    // analytics as the anonymous visitor this is. `AnalyticsProvider` left
    // `/enter` uninitialised on the promise that this page would do it.
    if (!token) {
      failConciergeSession();
      return;
    }

    // Redemption is a POST fired from here, on mount - never on GET of this
    // URL. WhatsApp fetches a shared link to build its preview card before the
    // recipient ever taps it, and crawlers do not execute JS, so a token
    // consumed by a plain page load would be spent before the salon saw it.
    const redeem = async () => {
      let result: Awaited<ReturnType<typeof sdk.store.concierge.redeem>>;

      // Only the redemption itself can mean "this link is no longer valid".
      // Everything after it happens on a session that already exists, so a
      // failure there must not be reported as a dead link.
      try {
        result = await sdk.store.concierge.redeem(token);
      } catch (error) {
        if (isFetchError(error)) {
          // A 4xx means the server read the link and refused it (expired,
          // replaced, unknown): nothing is left to retry with, and the
          // credentials must not outlive it in storage. A 5xx, a 429 or no
          // answer at all (offline) says nothing about the link, so those keep
          // them and a reload can try again.
          const status = error.status ?? 0;
          if (status >= 400 && status < 500 && status !== 429) {
            clearLinkCredentials();
            setServerRejectedLink(true);
          }
        }
        // The 4xx verdict on the link is the message to show; a 5xx or no
        // answer is ours to report, not the salon's to read.
        setMessage(
          handleActionError(error, {
            boundary: "concierge link redeem",
            fallback: FALLBACK_FAILURE_MESSAGE,
            trustClientErrors: true
          })
        );
        setState("failed");
        // Nobody was identified, so analytics starts as the anonymous visitor -
        // see `failConciergeSession`.
        failConciergeSession();
        return;
      }

      // Store the JWT before anything else: every subsequent request,
      // including the profile prefetch below, needs it.
      await sdk.client.setToken(result.token);

      // Hand the redeemed actor to analytics and let it decide what that
      // implies - suppression for an operator, funnel entry plus replay for a
      // salon. The ordering that makes the blackout airtight lives there, not
      // here, so a second concierge entry point (KAN-24) cannot get it wrong.
      beginConciergeSession(result.actor_type);

      // Store the ad hoc-add capability token (KAN-193), when this link
      // carried one. Only operator links carry `cap` - a salon-facing link
      // never does - so this is unconditional on the param being present,
      // not on `result.actor_type`: the token's presence is what the paste-in
      // entry point's gate actually checks (see `useIsConciergeOperator`).
      if (cap) {
        setCapabilityToken(cap);
      }

      // Redeemed: the link's credentials have done their job and must not
      // outlive it in storage. Kept until now so a reload could retry a failure.
      clearLinkCredentials();

      // Warm the profile so `authRoute`'s gate resolves from cache instead of
      // racing a fetch and bouncing the salon to /login.
      //
      // Reset first, not just refetched: `fetchQuery` honours `staleTime`, so
      // an in-SPA arrival at /enter would otherwise resolve the *previous*
      // identity's cached profile and warm the gate with the wrong customer.
      // (`LoginPage` invalidates for the same reason.) Fresh loads from
      // WhatsApp are unaffected - nothing is persisted across a page load.
      //
      // Reset, never `removeQueries` (KAN-254): `AnalyticsProvider` holds a
      // `useProfile` observer that was mounted on /enter before the token
      // existed, so it sits on an errored query. Removing that query orphans the
      // observer - it keeps watching the dead one and never sees the fresh
      // profile, so `$identify` was only reached when a later re-render happened
      // to rebind it. Resetting keeps the same query, and its observers, and
      // refetches it with the new token.
      //
      // Best-effort: the session is already minted, so a failed prefetch costs
      // the salon a redundant fetch behind the gate, not entry. Telling them the
      // link is dead at this point would be plainly false.
      try {
        await queryClient.resetQueries({ queryKey: profileQuery().queryKey });
        await queryClient.fetchQuery(profileQuery());
      } catch {
        // Deliberately swallowed - `authRoute` will fetch it again.
      }

      // The approval email's link (KAN-291) asks for the first password before
      // anything else. Keyed on `next=activate` so it keeps the "Activate your
      // account" copy. A WhatsApp concierge link also redeems for a salon with
      // no password; that one heads for its cart and `authRoute` sends it to
      // set a password on the way (KAN-306), keeping the cart as the target.
      // Never for an operator, who is shopping on a salon's behalf.
      if (isActivation && result.actor_type === "salon_user") {
        navigate({ to: ACTIVATE_PATH, replace: true });
        return;
      }

      // Straight to the curated cross-supplier comparison - the view the whole
      // WhatsApp journey exists to show them (KAN-38 made this addressable).
      navigate({
        to: "/cart",
        search: { tab: "review-matrix" },
        replace: true
      });
    };

    void redeem();
  }, [token, cap, isActivation, navigate]);

  return (
    <div className="bg-ui-bg-subtle flex min-h-dvh w-dvw items-center justify-center">
      <div className="m-4 flex w-full max-w-[320px] flex-col items-center text-center">
        <AvatarBox checked={false} />

        {state === "redeeming" ? (
          <div className="flex flex-col items-center gap-y-1">
            <Heading>Opening your cart…</Heading>
            <Text size="small" className="text-ui-fg-subtle">
              One moment while we sign you in.
            </Text>
          </div>
        ) : (
          <div className="flex w-full flex-col items-center gap-y-4">
            <div className="flex flex-col items-center gap-y-1">
              {isActivation ? (
                <>
                  <Heading>
                    {linkIsDead
                      ? "This activation link is no longer valid"
                      : "We couldn't open your activation link"}
                  </Heading>
                  <Text size="small" className="text-ui-fg-subtle">
                    {linkIsDead
                      ? "It may have expired, or been replaced by a newer one. If you have already activated your account, sign in. Otherwise contact us and we'll send you a new link."
                      : "Something went wrong on our side. Please try again in a moment, or contact us if it keeps happening."}
                  </Text>
                </>
              ) : (
                <>
                  <Heading>This link is no longer valid</Heading>
                  <Text size="small" className="text-ui-fg-subtle">
                    {state === "missing-token"
                      ? "This link is missing its code. Ask Caroline for a fresh link."
                      : message}
                  </Text>
                </>
              )}
            </div>

            {isActivation && linkIsDead && (
              // `asChild`: the link is the button. A <button> nested in an <a>
              // is invalid HTML and gives screen readers two controls.
              <Button className="w-full" variant="primary" asChild>
                <Link to="/login">Sign in</Link>
              </Button>
            )}

            <Button
              className="w-full"
              variant={isActivation && linkIsDead ? "secondary" : "primary"}
              asChild
            >
              <SupportWhatsAppLink profile={null} supportPath={supportPath}>
                Message us on WhatsApp
              </SupportWhatsAppLink>
            </Button>

            {isActivation && linkIsDead ? (
              <Text size="small" className="text-ui-fg-muted">
                Forgot your password?{" "}
                <Link to="/reset-password" className={STANDALONE_LINK_CLASS}>
                  Reset it
                </Link>
              </Text>
            ) : (
              <Text size="small" className="text-ui-fg-muted">
                Already have a password?{" "}
                <Link to="/login" className={STANDALONE_LINK_CLASS}>
                  Sign in
                </Link>
              </Text>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
